<template>
  <!-- Navigation Bar -->
  <nav class="navbar navbar-expand-lg navbar-light bg-white border-bottom">
    <div class="container-fluid px-3 px-md-4">
      <!-- Logo and title - always on the left -->
      <div class="d-flex align-items-center">
        <a class="navbar-brand p-0" href="#">
          <img src="@/assets/logotechnopaws.png" alt="PawPulse" height="40">
          <span class="ms-2 fw-bold d-none d-sm-inline" style="color: var(--primary-blue);">{{ translate('app.title', 'PawPulse') }}</span>
        </a>
      </div>

      <!-- Controls on the right -->
      <div class="d-flex align-items-center">
        <!-- Credits - visible on mobile and desktop -->
        <div class="me-3">
          <CreditsBadge />
        </div>
        
        <!-- Purchase credits button - only desktop -->
        <div class="purchase-credits-badge d-none d-md-block me-3">
          <span 
            class="badge rounded-pill bg-purchase" 
            role="button"
            @click="showPaymentModal = true"
          >
            {{ translate('payment.buy_credits', 'Purchase credits') }}
          </span>
        </div>
        
        <!-- User menu -->
        <div class="btn-group">
          <button 
            type="button" 
            class="btn btn-light dropdown-toggle d-flex align-items-center w-100" 
            data-bs-toggle="dropdown" 
            aria-expanded="false"
          >
            <i class="bi bi-person-circle"></i>
            <span class="d-none d-md-inline ms-2 text-truncate" style="max-width: 200px; display: inline-block;">{{ user?.email }}</span>
            <span class="d-md-none ms-2">{{ user?.email.split('@')[0] }}</span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <!-- Mobile version - Purchase credits button -->
            <li class="d-md-none px-3 py-2">
              <button class="btn btn-primary w-100" @click="showPaymentModal = true">
                <i class="bi bi-plus-circle me-2"></i>
                {{ translate('payment.buy_credits', 'Buy credits') }}
              </button>
            </li>
            <li class="d-md-none"><hr class="dropdown-divider m-0"></li>
            
            <!-- User options -->
            <li>
              <button class="dropdown-item" @click="resetPassword">
                <i class="bi bi-key me-2"></i>{{ translate('auth.reset_password', 'Reset Password') }}
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="openReviewLinkSettings">
                <i class="bi bi-link-45deg me-2"></i>{{ translate('settings.review_link', 'Business review link') }}
              </button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <button class="dropdown-item text-danger" @click="logout">
                <i class="bi bi-box-arrow-right me-2"></i>{{ translate('auth.logout', 'Logout') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>

  <div class="container-fluid">
    <div class="row h-100">
      <!-- Customer Sidebar -->
      <div class="col-md-4 col-lg-2 border-end pt-3 vh-100 overflow-auto d-none d-lg-block">
        <div class="list-group list-group-flush">
          <h5 class="mb-4">{{ translate('customers.title', 'Customers') }}</h5>
          
          <!-- Customer Search -->
          <div class="mb-3 position-relative">
            <div class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input 
                type="text" 
                class="form-control" 
                :placeholder="translate('customers.search', 'Search customers...')"
                v-model="customerSearchQuery"
                @input="handleCustomerSearch"
                @focus="showCustomerSearchResults = true"
              />
              <button 
                v-if="customerSearchQuery" 
                class="btn btn-outline-secondary" 
                type="button"
                @click="clearCustomerSearch"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            
            <!-- Search Results Dropdown -->
            <div 
              v-if="showCustomerSearchResults && customerSearchQuery" 
              class="search-results-dropdown"
              ref="searchResults"
            >
              <div v-if="filteredCustomers.length > 0">
                <div 
                  v-for="customer in filteredCustomers" 
                  :key="customer.id"
                  class="search-result-item"
                  @click="selectCustomer(customer); showCustomerSearchResults = false"
                >
                  <div class="d-flex flex-column">
                    <span class="fw-bold">{{ customer.name }}</span>
                    <span class="text-muted small">{{ customer.phone }}</span>
                    <span v-if="customer.pets && customer.pets.length" class="text-muted small">
                      <i class="bi bi-heart me-1"></i>{{ customer.pets.join(', ') }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="search-result-item text-center text-muted">
                {{ translate('customers.no_results', 'No customers found') }}
              </div>
            </div>
          </div>
          
          <div 
            v-for="customer in filteredCustomers" 
            :key="customer.id"
            class="card mb-2 p-0"
            :class="{ 
              'border-primary bg-primary bg-opacity-10': selectedCustomer?.id === customer.id 
            }"
            @click="selectCustomer(customer)"
          >
            <div class="card-body py-1 m-2">
              <button 
                type="button"
                class="btn p-0 w-100 d-flex justify-content-between align-items-center"
                
              >
                <span>{{ customer.name }}</span>
                <div class="rating-circle">
                  <span class="rating-text">{{ getAverageRating(customer.id) }}</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="col-12 col-lg-10 pt-3 vh-100 overflow-auto">
        <!-- Overall average rating card - full width and highlighted -->
        <div class="row mb-4">
          <div class="col-6 mb-3">
            <div class="card p-3 text-center h-100" style="background-color: #f0f8ff; border: 1px solid #b8daff; border-left: 5px solid #007bff;">
              <div class="small text-muted mb-2">{{ translate('dashboard.overall_store_rating', 'Overall Store Rating') }}</div>
              <div class="d-flex flex-column flex-md-row justify-content-center align-items-center">
                <div class="display-3 fw-bold me-md-2 mb-2 mb-md-0 text-center">{{ getOverallAverageRating }}</div>
                <div class="d-flex align-items-center justify-content-center">
                  <template v-for="i in 5" :key="i">
                    <i 
                      class="bi" 
                      :class="i <= Math.round(getOverallAverageRating) ? 'bi-star-fill text-warning' : 'bi-star text-muted'"
                      style="font-size: 1.5rem; margin-right: 3px;"
                    ></i>
                  </template>
                </div>
              </div>
              <div class="small text-muted mt-2">{{ translate('dashboard.based_on', 'Based on') }} {{ getRatedInteractionsCount }} {{ translate('dashboard.rated_interactions', 'rated interactions') }}</div>
            </div>
          </div>
          <div class="col-6 mb-3">
            <div 
              class="card p-3 text-center h-100" 
              style="background-color: #f0f8ff; border: 1px solid #b8daff; border-left: 5px solid #007bff;"
              :class="{ 'active-filter': ratingFilter === 'pending' }"
              @click="filterByRating('pending')"
              role="button"
            >
              <div class="small text-muted mb-2">{{ translate('dashboard.total_pending_cases', 'Total pending cases') }}</div>
              <div class="d-flex justify-content-center align-items-center">
                <div class="d-flex align-items-center">
                  <div class="display-3 fw-bold me-2">{{ getPendingCasesCount }}</div>
                </div>
              </div>
              <div class="small text-muted mt-2">{{ translate('dashboard.low_ratings_requests', 'Low ratings and customer requests') }}</div>
            </div>
          </div>
        </div>
        
        <!-- Statistics Section -->
        <div class="filter-section mb-2">
          <h5 class="text-center mb-3"><i class="bi bi-funnel me-2"></i>{{ translate('dashboard.click_below_to_filter', 'Click below to filter') }}</h5>
        </div>
        <div class="row mb-4">
          <div class="col-6 col-md-3 mb-3">
            <div 
              class="card p-3 text-center" 
              :class="{ 'active-filter': !selectedCustomer && !ratingFilter }"
              @click="clearAllFilters"
              role="button">
              <div class="small text-muted mb-2">{{ translate('dashboard.total_interactions', 'Total Interactions') }}</div>
              <div class="display-5 fw-bold">{{ interactions?.length }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div 
              class="card p-3 text-center" 
              style="background-color: #ffe6e6; border: 1px solid #ffc9c9;"
              :class="{ 'active-filter': ratingFilter === 'low' }"
              @click="filterByRating('low')"
              role="button"
            >
              <div class="small text-muted mb-2">{{ translate('dashboard.low_rating', 'Low Rating (1-2)') }}</div>
              <div class="display-5 fw-bold">{{ getRatings(1, 2) }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div 
              class="card p-3 text-center" 
              style="background-color: #ffe0cc; border: 1px solid #ffd699;"
              :class="{ 'active-filter': ratingFilter === 'mid' }"
              @click="filterByRating('mid')"
              role="button"
            >
              <div class="small text-muted mb-2">{{ translate('dashboard.mid_rating', 'Mid Rating (3)') }}</div>
              <div class="display-5 fw-bold">{{ getRatings(3, 3) }}</div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div 
              class="card p-3 text-center" 
              style="background-color: #dff0df; border: 1px solid #c6efce;"
              :class="{ 'active-filter': ratingFilter === 'high' }"
              @click="filterByRating('high')"
              role="button"
            >
              <div class="small text-muted mb-2">{{ translate('dashboard.high_rating', 'High Rating (4-5)') }}</div>
              <div class="display-5 fw-bold">{{ getRatings(4, 5) }}</div>
            </div>
          </div>
        </div>
        
        <!-- Interactions Section -->
        <div class="col-md-12">
          <h3 class="mb-4">{{ getInteractionsTitle }}</h3>
          <button 
            v-if="selectedCustomer || ratingFilter"
            class="btn btn-outline-primary btn-sm me-2"
            @click="clearAllFilters"
          >
            {{ translate('dashboard.clear_filters', 'Clear All Filters') }}
          </button>
          <div class="ms-2" v-if="ratingFilter">
            <button type="button" class="btn-close btn-close-white ms-2" aria-label="Close" @click="clearRatingFilter"></button>
          </div>
          <div class="d-flex justify-content-end mb-4">
            <button class="btn btn-outline-primary me-2" @click="showUploadInteractionsModal = true" :disabled="isDemo || credits <= 0">
              <i class="bi bi-upload me-2"></i>{{ translate('dashboard.upload_csv', 'Upload CSV') }}
            </button>
            <button class="btn btn-primary" @click="handleNewInteraction">
              <i class="bi bi-plus-lg me-2"></i>{{ translate('dashboard.new_interaction', 'New Interaction') }}
            </button>
          </div>

          <div class="d-none d-lg-block">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th scope="col">{{ translate('dashboard.service_time', 'Service Time') }}</th> 
                  <th scope="col">{{ translate('dashboard.customer', 'Customer') }}</th>
                  <th scope="col">{{ translate('dashboard.pet_name', 'Pet Name') }}</th>
                  <th scope="col">{{ translate('dashboard.service', 'Service') }}</th>
                  <th scope="col">{{ translate('dashboard.rating', 'Rating') }}</th>
                  <th scope="col">{{ translate('dashboard.view_details', 'View Details') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="interaction in filteredInteractions" 
                  :key="interaction.id"
                  :class="{ 'table-danger': interaction.alertTriggered }"
                  style="cursor: pointer; position: relative;"
                  @click="showInteractionDetails(interaction)"
                >
                   <td>
                    <div class="d-flex align-items-center">
                      <i class="bi bi-calendar-event me-1"></i>
                      {{  interaction.serviceDateTime ? formatDate(interaction.serviceDateTime) : translate('common.na', 'N/A') }}
                    </div>
                  </td> 
                  <td>
                    <div class="d-flex align-items-center">
                      {{ getCustomerName(interaction.customerId, interaction) }}
                      <i v-if="interaction.alertTriggered" class="bi bi-exclamation-triangle-fill text-danger ms-2" title="Attention Required"></i>
                    </div>
                  </td>
                  <td>{{ getPetNames(interaction.customerId, interaction) }}</td>
                  <td>{{ interaction.service || translate('common.na', 'N/A') }}</td>
                  <!-- <td>{{ interaction.summary || translate('common.na', 'N/A') }}</td> -->
                  <td>
                    <span class="badge rounded-pill" :class="interaction.alertTriggered ? 'bg-danger' : 'bg-primary'">
                      <i class="bi bi-star-fill me-1"></i>
                      {{ interaction.rating || translate('common.na', 'N/A') }}
                    </span>
                  </td>
                  <td>
                    <button 
                      class="btn btn-sm"
                      :class="interaction.alertTriggered ? 'btn-danger' : 'btn-primary'"
                      @click="showInteractionDetails(interaction)"
                    >
                      {{ translate('dashboard.view_details', 'View Details') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-lg-none">
            <div class="list-group">
              <div
                v-for="interaction in filteredInteractions" 
                :key="interaction.id"
                class="list-group-item list-group-item-action clickable-item"
                :class="{ 'list-group-item-danger': interaction.alertTriggered }"
                @click="showInteractionDetails(interaction)"
              >
                <div class="d-flex w-100 justify-content-between">
                  <h5 class="mb-1">
                    {{ getCustomerName(interaction.customerId, interaction) }}
                    <i v-if="interaction.alertTriggered" class="bi bi-exclamation-triangle-fill text-danger ms-2" title="Attention Required"></i>
                  </h5>
                  <div class="text-end">
                    <small class="text-muted d-block">{{ translate('dashboard.service_time', 'Hora del servicio') }}</small>
                    <small class="text-dark">{{ formatDate(interaction.serviceDateTime) }}</small>
                  </div>
                </div>

                <div class="d-flex w-100 justify-content-between">                
                  <p class="mb-1">
                    <span class="badge rounded-pill" :class="interaction.alertTriggered ? 'bg-danger' : 'bg-primary'">
                      <i class="bi bi-star-fill me-1"></i>
                      {{ interaction.rating || translate('common.na', 'N/A') }}
                    </span>
                  </p>
                <button 
                      class="btn rounded-pill"
                      :class="interaction.alertTriggered ? 'btn-danger' : 'btn-primary'"
                      @click="showInteractionDetails(interaction)"
                    >
                      {{ translate('dashboard.view_details', 'View Details') }}
                    </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <NewInteractionModal
    v-model:show="showNewInteractionModal"
    @interaction-created="handleNewInteraction"
  />

  <ChangePasswordModal
    v-if="showChangePasswordModal"
    @close="showChangePasswordModal = false"
    @password-changed="handlePasswordChanged"
  />

  <InteractionDetailsModal 
    :interactionId="selectedInteraction?.id" 
    v-model:show="showInteractionDetailsModal" 
  />

  <AssistanceAlertModal
    :show="showAssistanceModal"
    :interaction="assistanceInteraction"
    @contact="handleAssistanceContact"
    @close="handleAssistanceClose"
    @new-interaction="handleAssistanceNewInteraction"
  />
  
  <UploadInteractionsModal
    v-model:show="showUploadInteractionsModal"
    @purchase-credits="showPaymentModal = true"
  />
  
  <!-- Customer Selection Modal -->
  <CustomerSelectionModal 
    ref="customerSelectionModal"
    @customer-selected="handleCustomerSelected"
  />

  <!-- Reset Password Modal -->
  <ResetPasswordModal
    v-model="showResetPasswordModal"
    :default-email="getUserEmail()"
    :logout-after-send="true"
    @logout="logout"
  />

  <!-- No Credits Modal -->
  <NoCreditsModal
    v-model:show="showNoCreditsModal"
    @purchase-credits="handlePurchaseCredits"
    @purchase="handlePurchaseCredits"
  />

  <!-- Purchase Credits Modal -->
  <EpaycoPaymentModal
    v-model:show="showPaymentModal"
  />

  <div v-if="showReviewLinkModal" class="review-link-overlay" role="dialog" aria-modal="true" aria-labelledby="business-review-title" @click.self="showReviewLinkModal = false">
    <div class="review-link-dialog">
      <div class="review-link-card">
        <form @submit.prevent="saveReviewLink">
          <div class="review-link-header">
            <h5 id="business-review-title">{{ translate('settings.review_link', 'Business review link') }}</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="showReviewLinkModal = false"></button>
          </div>
          <div class="review-link-body">
            <label for="business-review-link" class="form-label">{{ translate('settings.review_link_description', 'Add the review link for your business') }}</label>
            <input
              id="business-review-link"
              v-model="reviewLinkInput"
              @input="reviewLinkSaved = false"
              class="form-control"
              type="url"
              placeholder="https://"
              autocomplete="url"
            >
            <div v-if="reviewLinkError" class="invalid-feedback d-block">{{ reviewLinkError }}</div>
            <div v-if="reviewLinkSaved" class="alert alert-success mt-3 mb-0" role="status">
              {{ translate('settings.review_link_saved', 'Review link saved successfully') }}
            </div>
            <div class="form-text">{{ translate('settings.review_link_hint', 'Customers will see this link after positive feedback. Leave blank to remove it.') }}</div>
            <div v-if="reviewLink" class="d-flex align-items-center gap-2 mt-3">
              <a :href="reviewLink" target="_blank" rel="noopener noreferrer" class="text-truncate">{{ reviewLink }}</a>
              <button type="button" class="btn btn-outline-primary btn-sm flex-shrink-0" @click="copyReviewLink">
                {{ reviewLinkCopied ? translate('settings.link_copied', 'Copied') : translate('settings.copy_link', 'Copy link') }}
              </button>
            </div>
          </div>
          <div class="review-link-footer">
            <button type="button" class="btn btn-outline-secondary" @click="showReviewLinkModal = false">{{ translate('messages.cancel', 'Cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="savingReviewLink">
              <span v-if="savingReviewLink" class="spinner-border spinner-border-sm me-2"></span>
              {{ translate('messages.save', 'Save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <button 
    class="btn btn-primary btn-lg rounded-circle position-fixed floating-btn"
    @click="openCustomerSelectionModal"
  >
    <i class="bi bi-person-lines-fill fs-4"></i>
  </button>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { setTawkAttributes } from '@/utils/tawk'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Dropdown } from 'bootstrap'
import { auth, db } from '@/config/firebase'
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore'
import NewInteractionModal from '@/components/NewInteractionModal.vue'
import ChangePasswordModal from '@/components/ChangePasswordModal.vue'
import InteractionDetailsModal from '@/components/InteractionDetailsModal.vue'
import UploadInteractionsModal from '@/components/UploadInteractionsModal.vue'
import CustomerSelectionModal from '@/components/CustomerSelectionModal.vue'
import CreditsBadge from '@/components/CreditsBadge.vue'
import NoCreditsModal from '@/components/NoCreditsModal.vue'
import EpaycoPaymentModal from '@/components/EpaycoPaymentModal.vue'
import ResetPasswordModal from '@/components/ResetPasswordModal.vue'
import AssistanceAlertModal from '@/components/AssistanceAlertModal.vue'

export default {
  name: 'HomeView',
  components: {
    NewInteractionModal,
    ChangePasswordModal,
    InteractionDetailsModal,
    UploadInteractionsModal,
    CustomerSelectionModal,
    CreditsBadge,
    NoCreditsModal,
    EpaycoPaymentModal,
    ResetPasswordModal,
    AssistanceAlertModal
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const showNewInteractionModal = ref(false)
    const showChangePasswordModal = ref(false)
    const showInteractionDetailsModal = ref(false)
    const showUploadInteractionsModal = ref(false)
    const showNoCreditsModal = ref(false)
    const showResetPasswordModal = ref(false)
    const showPaymentModal = ref(false)
    const showReviewLinkModal = ref(false)
    const reviewLinkInput = ref('')
    const reviewLinkError = ref('')
    const reviewLinkSaved = ref(false)
    const reviewLinkCopied = ref(false)
    const savingReviewLink = ref(false)
    const reviewLink = computed(() => {
      const profile = store.state.users.find(profile => profile.userId === user.value?.uid)
      return profile?.reviewLink || ''
    })
    const customerSelectionModal = ref(null)
    const selectedCustomer = ref(null)
    const selectedInteraction = ref(null)
    const user = computed(() => store.state.user)
    const customers = computed(() => store.state.customers)
    const interactions = computed(() => store.state.interactions)
    const credits = computed(() => store.state.credits || 0)
    const isDemo = computed(() => store.state.demo === true)
    const customerSearchQuery = ref('')
    const showCustomerSearchResults = ref(false)
    const searchResults = ref(null)
    const ratingFilter = ref(null) // Stores 'low', 'mid', 'high', or null
    const realTimeListeners = ref([]) // Array to store Firestore listener unsubscribe functions
    const showAssistanceModal = ref(false)
    const assistanceInteraction = ref(null)
    const assistanceListeners = ref([])
    const lastAssistanceState = new Map() // phone → serialized state string
    let assistanceInitialized = false
    const lastInteractionAlertState = new Map()
    let interactionAlertsInitialized = false

    // Translation function
    const translate = (key, fallback) => store.getters.translate(key, fallback)

    onMounted(() => {
      // Initialize all dropdowns
      document.querySelectorAll('.dropdown-toggle').forEach(el => {
        new Dropdown(el)
      })
      
      // Add click outside listener for search dropdown
      document.addEventListener('click', handleClickOutside)
      
      // Load initial data and user language preference
      loadInitialData()
      loadUserLanguagePreference()

      
      // Set tawk.to attributes for the 'app' view
      setTawkAttributes('app')
    })
    
    // Function to load initial data
    const loadInitialData = async () => {
      try {
        await Promise.all([
          store.dispatch('fetchUsers'),
          store.dispatch('fetchCustomers'),
          store.dispatch('fetchInteractions'),
        ])
      } catch (error) {
        console.error('Error loading initial data:', error)
      }
    }
    
    // Function to load the user's language preference, real-time listeners for credits and demo status from Firestore
    const loadUserLanguagePreference = async () => {
    try {
      const user = auth.currentUser
      if (!user) return
      const q = query(collection(db, 'users'), where('userId', '==', user.uid))
      
      // Initial load and setup real-time listener
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data()
          
          // Load user's preferred language (only on initial load)
          if (userData.preferredLanguage && !store.state.translations) {
            store.dispatch('fetchTranslations', userData.preferredLanguage)
          }
          
          // Update credits in real time
          const credits = userData.credits !== undefined ? userData.credits : 0
          store.commit('setCredits', credits)
          
          // Update demo status in real time
          const demoStatus = userData.demo !== undefined ? userData.demo : null
          store.commit('setDemoStatus', demoStatus)   
        }
      }, (error) => {
        console.error('Error in real-time listener:', error)
      })
      
      // Store the unsubscribe function to clean up on component unmount
      realTimeListeners.value.push(unsubscribe)
    } catch (error) {
      console.error('Error setting up user data listener:', error)
    }
  }

    onUnmounted(() => {
      // Remove click outside listener
      document.removeEventListener('click', handleClickOutside)
      // Clean up real-time listeners
      realTimeListeners.value.forEach(listener => listener())
      // Clean up assistance listeners
      assistanceListeners.value.forEach(u => u())
    })

    // Serialize the relevant state of an ASSISTANCE doc to detect create + updates
    const assistanceDocState = (data) => {
      const conversation = Array.isArray(data.conversation) ? data.conversation : []
      return {
        date: data.date || '',
        conversationLength: conversation.length,
        customerMessages: conversation.filter(message => message.role === 'customer').length,
        alertTriggered: data.alertTriggered === true
      }
    }

    const assistanceStateChanged = (previous, current) =>
      !previous ||
      previous.date !== current.date ||
      previous.conversationLength !== current.conversationLength ||
      previous.alertTriggered !== current.alertTriggered

    const isNewNegativeAlert = (previous, current) =>
      current.alertTriggered && previous?.alertTriggered !== true

    // Setup direct Firestore listeners for each customer's ASSISTANCE doc
    const setupAssistanceListeners = (customerList) => {
      assistanceListeners.value.forEach(u => u())
      assistanceListeners.value = []
      const phones = new Set()
      customerList.forEach(c => {
        if (c.phone) {
          const plain = (c.phone + '').replace('whatsapp:', '').replace('+', '').replace(/\D/g, '')
          if (plain) phones.add(plain)
        }
      })
      phones.forEach(phone => {
        const docId = `${phone}_ASSISTANCE`
        const unsub = onSnapshot(doc(db, 'interactions', docId), (snap) => {
          if (!snap.exists()) {
            lastAssistanceState.set(phone, null)
            return
          }
          const data = snap.data()
          const currentState = assistanceDocState(data)
          const lastState = lastAssistanceState.get(phone)

          if (lastState === undefined) {
            // First snapshot — save state silently, never alert for pre-existing docs
            lastAssistanceState.set(phone, currentState)
            return
          }

          // Doc was just created (lastState was null) or updated (state changed)
          if (assistanceStateChanged(lastState, currentState)) {
            lastAssistanceState.set(phone, currentState)
            // Only show assistance modal if alertTriggered is true (negative feedback)
            if (isNewNegativeAlert(lastState, currentState)) {
              assistanceInteraction.value = { id: snap.id, ...data }
              showAssistanceModal.value = true
            }
          }
        })
        assistanceListeners.value.push(unsub)
      })
    }

    // Also watch the store for _ASSISTANCE docs that carry userId
    watch(interactions, (list) => {
      if (!assistanceInitialized) {
        list.filter(i => i.id?.endsWith('_ASSISTANCE')).forEach(i => {
          const phone = (i.id + '').split('_')[0]
          lastAssistanceState.set(phone, assistanceDocState(i))
        })
        assistanceInitialized = true
        return
      }
      for (const i of list) {
        if (i.id?.endsWith('_ASSISTANCE')) {
          const phone = (i.id + '').split('_')[0]
          const currentState = assistanceDocState(i)
          const lastState = lastAssistanceState.get(phone)
          if (lastState !== undefined && assistanceStateChanged(lastState, currentState)) {
            lastAssistanceState.set(phone, currentState)
            // Only show assistance modal if alertTriggered is true (negative feedback)
            if (isNewNegativeAlert(lastState, currentState)) {
              assistanceInteraction.value = i
              showAssistanceModal.value = true
              break
            }
          }
        }
      }
    })

    watch(interactions, (list) => {
      const monitored = list.filter(i => i.id && !i.id.endsWith('_ASSISTANCE'))
      if (!interactionAlertsInitialized) {
        if (!monitored.length) return
        monitored.forEach(i => lastInteractionAlertState.set(i.id, assistanceDocState(i)))
        interactionAlertsInitialized = true
        return
      }

      for (const interaction of monitored) {
        const currentState = assistanceDocState(interaction)
        const lastState = lastInteractionAlertState.get(interaction.id)
        if (lastState === undefined) {
          lastInteractionAlertState.set(interaction.id, currentState)
          if (isNewNegativeAlert(lastState, currentState)) {
            assistanceInteraction.value = interaction
            showAssistanceModal.value = true
            break
          }
          continue
        }

        if (assistanceStateChanged(lastState, currentState)) {
          lastInteractionAlertState.set(interaction.id, currentState)
          if (isNewNegativeAlert(lastState, currentState)) {
            assistanceInteraction.value = interaction
            showAssistanceModal.value = true
            break
          }
        }
      }
    })

    // Setup assistance listeners once customers are available
    watch(() => store.state.customers, (list) => {
      if (list.length > 0) setupAssistanceListeners(list)
    }, { immediate: true })

    const handleAssistanceContact = () => {
      // Open the ASSISTANCE chat and close the modal
      if (assistanceInteraction.value) {
        selectedInteraction.value = { id: assistanceInteraction.value.id }
        showInteractionDetailsModal.value = true
        showAssistanceModal.value = false
      }
    }

    const handleAssistanceNewInteraction = () => {
      showAssistanceModal.value = false
      showNewInteractionModal.value = true
    }

    const handleAssistanceClose = () => {
      showAssistanceModal.value = false
    }

    // Function to handle clicks outside of search dropdown
    const handleClickOutside = (event) => {
      if (searchResults.value && !searchResults.value.contains(event.target)) {
        showCustomerSearchResults.value = false
      }
    }

    // Statistics computations
    const getLowRatingsCount = computed(() => {
      return interactions.value.filter(i => i.rating >= 1 && i.rating <= 2).length
    })
    
    const getMediumRatingsCount = computed(() => {
      return interactions.value.filter(i => i.rating >= 3 && i.rating <= 4).length
    })
    
    const getHighRatingsCount = computed(() => {
      return interactions.value.filter(i => i.rating === 5).length
    })
    
    const getRatedInteractionsCount = computed(() => {
      return interactions.value.filter(i => i.rating && i.rating > 0).length || 1
    })

    const filteredCustomers = computed(() => {
      if (!customerSearchQuery.value) return customers.value
      
      const query = customerSearchQuery.value.toLowerCase().trim();
      
      return customers.value.filter(customer => {
        // Search by name
        if (customer.name && customer.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search by phone
        let strPhone = customer.phone + ''
        if (strPhone && strPhone.includes(query)) {
          return true;
        }
        
        // Search by pet name (if pets array exists)
        if (customer.pets && Array.isArray(customer.pets)) {
          return customer.pets.some(pet => 
            pet && pet.toLowerCase().includes(query)
          );
        }
        
        return false;
      });
    })

    const filteredInteractions = computed(() => {
      let result = interactions.value;
      
      // Filter by customer if one is selected
      if (selectedCustomer.value) {
        result = result.filter(i => i.customerId === selectedCustomer.value.id);
      }
      
      // Apply rating filter if set
      if (ratingFilter.value) {
        if (ratingFilter.value === 'low') {
          result = result.filter(i => i.rating >= 1 && i.rating <= 2);
        } else if (ratingFilter.value === 'mid') {
          result = result.filter(i => i.rating === 3);
        } else if (ratingFilter.value === 'high') {
          result = result.filter(i => i.rating >= 4 && i.rating <= 5);
        } else if (ratingFilter.value === 'pending') {
          result = result.filter(i => 
            // Rating is 1, 2, or 3
            (i.rating >= 1 && i.rating <= 3) || 
            // OR inactive without rating
            (i.active === false && (!i.rating || i.rating === 0))
          );
        }
      }
      
      return result;
    })

    const showAllInteractions = () => {
      selectedCustomer.value = null
    }

    const selectCustomer = (customer) => {
      selectedCustomer.value = selectedCustomer.value?.id === customer.id ? null : customer
    }

    const handleNewInteraction = async () => {
      // Verify if the user has credits available
      if (credits.value <= 0) {
        // If no credits, show the warning modal
        showNoCreditsModal.value = true
      } else {
        // If credits available, show the new interaction modal
        showNewInteractionModal.value = true
        // Update data
        await store.dispatch('fetchUsers')
      }
    }
    
    const handlePurchaseCredits = () => {
      showNoCreditsModal.value = false
      showPaymentModal.value = true
    }

    const changePassword = () => {
      showChangePasswordModal.value = true
    }
    
    const resetPassword = () => {
      showResetPasswordModal.value = true
    }

    const openReviewLinkSettings = () => {
      reviewLinkInput.value = reviewLink.value
      reviewLinkError.value = ''
      reviewLinkSaved.value = false
      reviewLinkCopied.value = false
      showReviewLinkModal.value = true
    }

    const copyReviewLink = async () => {
      try {
        await navigator.clipboard.writeText(reviewLink.value)
        reviewLinkCopied.value = true
      } catch {
        reviewLinkError.value = translate('settings.copy_review_link_error', 'Could not copy the review link.')
      }
    }

    const saveReviewLink = async () => {
      const value = reviewLinkInput.value.trim()
      reviewLinkSaved.value = false
      if (value) {
        try {
          const url = new URL(value)
          if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid protocol')
        } catch {
          reviewLinkError.value = translate('settings.invalid_review_link', 'Enter a valid link starting with http:// or https://')
          return
        }
      }

      const profile = store.state.users.find(profile => profile.userId === user.value?.uid)
      if (!profile?.id) {
        reviewLinkError.value = translate('settings.save_review_link_error', 'Could not find your business profile.')
        return
      }

      savingReviewLink.value = true
      reviewLinkError.value = ''
      try {
        await updateDoc(doc(db, 'users', profile.id), { reviewLink: value })
        store.commit('setUsers', store.state.users.map(item =>
          item.id === profile.id ? { ...item, reviewLink: value } : item
        ))
        reviewLinkSaved.value = true
      } catch (error) {
        const code = error?.code || 'unknown'
        const detail = error?.message || ''
        console.error('Business review link save failed:', code)
        reviewLinkError.value = [
          translate('settings.save_review_link_error', 'Could not save the review link. Please try again.'),
          detail,
          code !== 'unknown' ? `(${code})` : ''
        ].filter(Boolean).join(' ')
      } finally {
        savingReviewLink.value = false
      }
    }
    
    // Function to get the user's email from Vuex or Firebase auth
    const getUserEmail = () => {
      // First try to get it from Vuex state
      if (user.value && user.value.email) return user.value.email
      // If not available, try to get it directly from Firebase auth
      if (auth.currentUser && auth.currentUser.email) return auth.currentUser.email
      // If we can't get it from any source, return an empty string
      return ''
    }

    const logout = async () => {
      try {
        await store.dispatch('logout')
        router.push({ name: 'Login' })
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getCustomerName = (customerId, interaction) => {
      const customer = customers.value.find(c => c.id === customerId)
      if (customer) return customer.name
      if (interaction && interaction.customerName) return interaction.customerName
      if (interaction && interaction.name) return interaction.name
      return 'N/A'
    }
    
    const getPetNames = (customerId, interaction) => {
      const customer = customers.value.find(c => c.id === customerId)
      if (customer && customer.pets && customer.pets.length) return customer.pets.join(', ')
      if (interaction && interaction.petName) return interaction.petName
      return 'N/A'
    }

    const showInteractionDetails = (interaction) => {
      // Only set the ID of the selected interaction
      selectedInteraction.value = { id: interaction.id }
      
      // Show the modal using Vue reactivity
      showInteractionDetailsModal.value = true
    }

    const handleCustomerSearch = () => {
      if (!customerSearchQuery.value) {
        showCustomerSearchResults.value = false
      }
    }

    const clearCustomerSearch = () => {
      customerSearchQuery.value = ''
      showCustomerSearchResults.value = false
    }

    const getAverageRating = (customerId) => {
      const customerInteractions = interactions.value.filter(i => i.customerId === customerId)
      if (customerInteractions && customerInteractions.length > 0 )
      //const sum = customerInteractions.reduce((acc, i) => acc + i.rating, 0)
      //return sum / customerInteractions.length
      return customerInteractions.length
    }

    const openCustomerSelectionModal = () => {
      if (customerSelectionModal.value) {
        customerSelectionModal.value.showModal()
      }
    }

    const handleCustomerSelected = (customer) => {
      selectCustomer(customer)
    }

    
    const getRatings = (minRating, maxRating) => {
      const customerInteractions = interactions.value.filter(i => i.rating >= minRating && i.rating <= maxRating)
      return customerInteractions.length
    }

    const getOverallAverageRating = computed(() => {
      // Filter interactions that have a rating
      const ratedInteractions = interactions.value.filter(i => i.rating && i.rating > 0)
      
      if (ratedInteractions.length === 0) {
        return 0
      }
      
      // Calculate the sum of all ratings
      const sum = ratedInteractions.reduce((acc, interaction) => parseInt(acc) + parseInt(interaction.rating), 0)
      
      // Return the average with 1 decimal place
      return (sum / ratedInteractions.length).toFixed(1)
    })

    const getInteractionsTitle = computed(() => {
      let title = '';
      
      if (selectedCustomer.value) {
        title += translate('dashboard.interactions_for', 'Interactions for customer') + ': ' + selectedCustomer.value.name;
      } else {
        title += translate('dashboard.all_interactions', 'All Interactions');
      }
      
      if (ratingFilter.value) {
        title += ` (${getRatingFilterLabel.value})`;
      }
      
      return title;
    })
    
    const getRatingFilterLabel = computed(() => {
      switch(ratingFilter.value) {
        case 'low': return translate('dashboard.low_rating', 'Low Rating (1-2)');
        case 'mid': return translate('dashboard.mid_rating', 'Mid Rating (3)');
        case 'high': return translate('dashboard.high_rating', 'High Rating (4-5)');
        case 'pending': return translate('dashboard.pending_cases', 'Pending Cases');
        default: return '';
      }
    })
     
    const filterByRating = (rating) => {
      // Toggle off if already active
      if (ratingFilter.value === rating) {
        ratingFilter.value = null;
      } else {
        ratingFilter.value = rating;
      }
    }
    
    const clearRatingFilter = () => {
      ratingFilter.value = null;
    }
    
    const clearAllFilters = () => {
      selectedCustomer.value = null;
      clearRatingFilter();
    }

    const getPendingCasesCount = computed(() => {
      return interactions.value.filter(i => 
        // Rating is 1, 2, or 3
        (i.rating >= 1 && i.rating <= 3) || 
        // OR inactive without rating
        (i.active === false && (!i.rating || i.rating === 0))
      ).length;
    })

    return {
      user,
      customers,
      interactions,
      selectedCustomer,
      selectedInteraction,
      customerSearchQuery,
      showCustomerSearchResults,
      searchResults,
      ratingFilter,
      showNewInteractionModal,
      showChangePasswordModal,
      showInteractionDetailsModal,
      showUploadInteractionsModal,
      showNoCreditsModal,
      showPaymentModal,
      showReviewLinkModal,
      reviewLinkInput,
      reviewLinkError,
      reviewLinkSaved,
      reviewLinkCopied,
      savingReviewLink,
      customerSelectionModal,
      filteredCustomers,
      filteredInteractions,
      selectCustomer,
      handleNewInteraction,
      handlePurchaseCredits,
      changePassword,
      logout,
      reviewLink,
      openReviewLinkSettings,
      copyReviewLink,
      saveReviewLink,
      formatDate,
      showAllInteractions,
      showInteractionDetails,
      getAverageRating,
      handleCustomerSearch,
      handleClickOutside,
      clearCustomerSearch,
      getCustomerName,
      getPetNames,
      getLowRatingsCount,
      getMediumRatingsCount,
      getHighRatingsCount,
      getRatedInteractionsCount,
      openCustomerSelectionModal,
      handleCustomerSelected,
      getRatings,
      getOverallAverageRating,
      filterByRating,
      clearRatingFilter,
      clearAllFilters,
      getInteractionsTitle,
      getRatingFilterLabel,
      getPendingCasesCount,
      translate,
      credits,
      isDemo,
      showResetPasswordModal,
      resetPassword,
      getUserEmail,
      showAssistanceModal,
      assistanceInteraction,
      handleAssistanceContact,
      handleAssistanceClose,
      handleAssistanceNewInteraction
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 2rem 0;
}

.search-results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.search-result-item {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background-color: #f8f9fa;
}

.rating-circle {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: var(--primary-blue);
  color: white;
  font-size: 1rem;
  font-weight: bold;
  margin-left: 0.5rem;
}
.home {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.list-group-item {
  cursor: pointer;
  border: none;
  padding: 0.75rem 1rem;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.active-filter {
  box-shadow: 0 0 0 6px rgba(13, 110, 253, 0.65) !important;
  cursor: pointer;
  transform: translateY(-2px);
  transition: all 0.2s ease;
}

.card[role="button"] {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
}

.card[role="button"]:before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(13, 110, 253, 0.5);
  transform: scaleX(0);
  transition: transform 0.2s ease;
}

.card[role="button"]:hover:before {
  transform: scaleX(1);
}

.card[role="button"]:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.badge {
  padding: 0.5em 0.8em;
  font-size: 0.9em;
  display: inline-flex;
  align-items: center;
}

.btn-close {
  font-size: 0.7em;
  padding: 0.25em;
}

.clickable-item {
  cursor: pointer;
  position: relative;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateX(-50%) translateY(10px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* styles for purchase credits badge */
.purchase-credits-badge {
  margin-right: 15px;
}

.bg-purchase {
  background-color: #8e44ad; /* Purple color */
  color: white;
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bg-purchase:hover {
  background-color: #9b59b6;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.review-link-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.68);
  backdrop-filter: blur(2px);
}

.review-link-dialog {
  width: 100%;
  max-width: 500px;
}

.review-link-card {
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.85rem;
  background-color: #fff;
  box-shadow: 0 1.25rem 3rem rgba(15, 23, 42, 0.3);
}

.review-link-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--primary);
  color: #fff;
}

.review-link-header h5 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.review-link-header .btn-close {
  flex: 0 0 auto;
  filter: brightness(0) invert(1);
}

.review-link-body {
  padding: 1.5rem 1.25rem;
  background-color: #fff;
  color: #212529;
}

.review-link-body .form-label {
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.review-link-body .form-control {
  min-height: 46px;
  border-color: #adb5bd;
  background-color: #fff;
}

.review-link-body .form-text {
  margin-top: 0.65rem;
  color: #5c6670;
}

.review-link-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem;
  background-color: #fff;
}

.review-link-footer .btn {
  min-width: 112px;
  min-height: 42px;
}

@media (max-width: 575.98px) {
  .review-link-footer .btn {
    flex: 1;
  }
}
</style>
